import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  IconButton,
  Divider,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DeleteIcon from '@mui/icons-material/Delete';
import { useCart } from '../../contexts/CartContext';
import { useNavigate } from 'react-router-dom';
import { getApiUrl, fetchOptions } from '../../config/api';

const PageTitle = styled(Typography)({
  fontFamily: 'Cormorant Garamond, serif',
  fontSize: '2.5rem',
  color: '#1a1a1a',
  marginBottom: '2rem',
  textAlign: 'center',
});

const CartItem = styled(Card)({
  marginBottom: '1rem',
  border: '1px solid #eee',
  boxShadow: 'none',
});

const CheckoutButton = styled(Button)({
  backgroundColor: '#1a1a1a',
  color: '#fff',
  padding: '1rem',
  marginTop: '2rem',
  '&:hover': {
    backgroundColor: '#c4a661',
  },
  '&:disabled': {
    backgroundColor: '#ccc',
  },
});

const Checkout = () => {
  const { cart, updateQuantity, removeFromCart } = useCart();
  const navigate = useNavigate();

  const getAbsoluteImageUrl = (relativeUrl) => {
    // Convert relative image URLs to absolute URLs
    if (!relativeUrl) return null;
    if (relativeUrl.startsWith('http')) return relativeUrl;
    return `${window.location.origin}${relativeUrl}`;
  };

  const handleCheckout = async () => {
    try {
      const response = await fetch(getApiUrl('/api/checkout'), {
        ...fetchOptions,
        method: 'POST',
        body: JSON.stringify({
          cart: cart.map(item => {
            // Calculate total cart value to determine if shipping should be added
            const cartTotal = cart.reduce((total, item) => total + item.price * item.quantity, 0);
            // Add shipping cost to first item if total is under $5000
            const isFirstItem = item.id === cart[0].id;
            const shippingCost = cartTotal < 5000 && isFirstItem ? 300 : 0;
            
            return {
              id: item.id,
              name: item.name,
              description: item.name,
              price: item.price + shippingCost, // Add shipping to first item only
              quantity: item.quantity,
              images: item.images ? [getAbsoluteImageUrl(item.images[0])] : [],
              category: item.category,
            };
          })
        }),
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const { url } = await response.json();
      window.location.href = url;
    } catch (error) {
      console.error('Error during checkout:', error);
    }
  };

  const calculateTotal = () => {
    return cart.reduce((total, item) => total + item.price * item.quantity, 0);
  };

  if (cart.length === 0) {
    return (
      <Container>
        <Box sx={{ py: 8 }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <PageTitle>Shopping Cart</PageTitle>
            <Typography align="center" color="textSecondary">
              Your cart is empty
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Button
                variant="contained"
                onClick={() => navigate('/')}
                sx={{
                  backgroundColor: '#1a1a1a',
                  '&:hover': { backgroundColor: '#c4a661' },
                }}
              >
                Continue Shopping
              </Button>
            </Box>
          </motion.div>
        </Box>
      </Container>
    );
  }

  return (
    <Container>
      <Box sx={{ py: 8 }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <PageTitle>Shopping Cart</PageTitle>

          <Grid container spacing={4}>
            <Grid item xs={12} md={8}>
              {cart.map((item) => (
                <CartItem key={item.id}>
                  <CardContent>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={3}>
                        <Box
                          component="img"
                          src={item.images ? item.images[0] : ''}
                          alt={item.name}
                          sx={{
                            width: '100%',
                            height: 'auto',
                            borderRadius: '4px',
                          }}
                        />
                      </Grid>
                      <Grid item xs={5}>
                        <Typography variant="h6">{item.name}</Typography>
                        <Typography color="textSecondary">
                          ${item.price}
                        </Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <IconButton
                            onClick={() => updateQuantity(item.id, item.quantity - 1)}
                            disabled={item.quantity <= 1}
                          >
                            <RemoveIcon />
                          </IconButton>
                          <Typography sx={{ mx: 2 }}>{item.quantity}</Typography>
                          <IconButton
                            onClick={() => updateQuantity(item.id, item.quantity + 1)}
                          >
                            <AddIcon />
                          </IconButton>
                        </Box>
                      </Grid>
                      <Grid item xs={1}>
                        <IconButton
                          onClick={() => removeFromCart(item.id)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Grid>
                    </Grid>
                  </CardContent>
                </CartItem>
              ))}
            </Grid>

            <Grid item xs={12} md={4}>
              <Card elevation={0} sx={{ border: '1px solid #eee', p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Order Summary
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography>Subtotal</Typography>
                  <Typography>${calculateTotal()}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography>Shipping</Typography>
                  <Typography>
                    {calculateTotal() < 5000 ? '$300' : 'Free'}
                    {calculateTotal() < 5000 && (
                      <Typography variant="caption" color="textSecondary" display="block">
                        (Free shipping on orders over $5,000)
                      </Typography>
                    )}
                  </Typography>
                </Box>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6">Total</Typography>
                  <Typography variant="h6">
                    ${calculateTotal() < 5000 ? calculateTotal() + 300 : calculateTotal()}
                  </Typography>
                </Box>
                <CheckoutButton
                  fullWidth
                  onClick={handleCheckout}
                  disabled={cart.length === 0}
                >
                  Proceed to Checkout
                </CheckoutButton>
              </Card>
            </Grid>
          </Grid>
        </motion.div>
      </Box>
    </Container>
  );
};

export default Checkout;
